<template>
  <div class="video-page">
    <main class="video-main">
      <label class="language-switcher">
        <span class="select-label">{{ $t('videoPage.selectLanguage') }}</span>
        <select
          :value="activeLanguage"
          :aria-label="$t('videoPage.selectLanguage')"
          @change="selectVideo($event.target.value)"
        >
          <option
          v-for="option in videoOptions"
          :key="option.locale"
            :value="option.locale"
          >
            {{ option.label }}
          </option>
        </select>
      </label>

      <section class="video-hero">
        <span class="eyebrow">{{ $t('videoPage.eyebrow') }}</span>
        <h1>{{ $t('videoPage.title') }}</h1>
        <p>{{ $t('videoPage.description') }}</p>
      </section>

      <section class="player-section" :aria-label="$t('videoPage.eyebrow')">
        <div class="player-card">
          <div class="player-meta">
            <span>{{ $t('videoPage.current') }}</span>
            <strong>{{ activeVideo.label }}</strong>
          </div>

          <div class="video-frame">
            <video
              v-show="!videoUnavailable"
              ref="videoPlayer"
              :key="activeLanguage"
              controls
              playsinline
              preload="metadata"
              @loadedmetadata="handleVideoReady"
              @error="handleVideoError"
            >
              <source :src="activeVideo.src" type="video/mp4">
            </video>

            <div v-if="videoUnavailable" class="video-placeholder" role="status">
              <div class="play-mark" aria-hidden="true">
                <span></span>
              </div>
              <h2>{{ placeholderTitle }}</h2>
              <p>{{ placeholderText }}</p>
            </div>
          </div>

          <p class="player-hint">
            <span class="hint-icon" aria-hidden="true">i</span>
            {{ $t('videoPage.hint') }}
          </p>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: 'VideoPage',
  data () {
    return {
      activeLanguage: this.$i18n.locale === 'zh' ? 'zh' : 'en',
      videoUnavailable: false,
      resumeTime: 0,
      resumePlayback: false
    }
  },
  computed: {
    videoOptions () {
      return [
        {
          locale: 'zh',
          label: this.$t('videoPage.chinese'),
          src: 'https://cos-acc-rec.nebula-ai.net/voide/NebulaRec-zh.mp4'
        },
        {
          locale: 'en',
          label: this.$t('videoPage.english'),
          src: 'https://cos-acc-rec.nebula-ai.net/voide/NebulaRec-us.mp4'
        }
      ]
    },
    activeVideo () {
      return this.videoOptions.find(item => item.locale === this.activeLanguage) || this.videoOptions[0]
    },
    placeholderTitle () {
      return this.videoUnavailable ? this.$t('videoPage.unavailableTitle') : ''
    },
    placeholderText () {
      return this.videoUnavailable ? this.$t('videoPage.unavailableText') : ''
    }
  },
  watch: {
    '$i18n.locale' (locale) {
      const normalizedLocale = locale === 'zh' ? 'zh' : 'en'
      if (normalizedLocale !== this.activeLanguage) {
        this.switchPlayer(normalizedLocale)
      }
    }
  },
  methods: {
    selectVideo (locale) {
      if (locale === this.activeLanguage) return

      this.$i18n.locale = locale
      this.switchPlayer(locale)
    },
    switchPlayer (locale) {
      const player = this.$refs.videoPlayer
      if (player) {
        this.resumeTime = player.currentTime || 0
        this.resumePlayback = !player.paused
      }

      this.videoUnavailable = false
      this.activeLanguage = locale
    },
    handleVideoReady () {
      const player = this.$refs.videoPlayer
      if (!player) return

      this.videoUnavailable = false
      if (this.resumeTime > 0) {
        player.currentTime = Math.min(this.resumeTime, Math.max(player.duration - 0.1, 0))
      }
      if (this.resumePlayback) {
        player.play().catch(() => {})
      }
      this.resumeTime = 0
      this.resumePlayback = false
    },
    handleVideoError () {
      this.videoUnavailable = true
      this.resumePlayback = false
    }
  }
}
</script>

<style scoped>
.video-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 50% 5%, rgba(50, 120, 253, 0.12), transparent 30%),
    #f9fafb;
}

.video-main {
  position: relative;
  width: min(1120px, 90%);
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
  padding: clamp(20px, 4vh, 42px) 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.video-hero {
  max-width: 760px;
  margin: 0 auto clamp(16px, 2.5vh, 26px);
  text-align: center;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border: 1px solid #d8e5ff;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  color: #3278fd;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.video-hero h1 {
  margin-top: clamp(10px, 1.8vh, 18px);
  color: #101828;
  font-size: clamp(30px, 4vw, 48px);
  font-weight: 700;
  line-height: 1.16;
  letter-spacing: -.035em;
}

.video-hero p {
  max-width: 660px;
  margin: clamp(8px, 1.4vh, 14px) auto 0;
  color: #667085;
  font-size: 16px;
  line-height: 1.6;
  text-align: center;
}

.player-section {
  width: min(980px, 92vw, 92vh);
  margin: 0 auto;
}

.language-switcher {
  position: absolute;
  top: clamp(14px, 2.5vh, 26px);
  right: 0;
  z-index: 2;
  display: block;
  width: fit-content;
  border-radius: 8px;
  background: rgba(255, 255, 255, .92);
  box-shadow: 0 4px 16px rgba(16, 24, 40, .05);
}

.language-switcher::after {
  position: absolute;
  top: 50%;
  right: 10px;
  width: 6px;
  height: 6px;
  border-right: 1.5px solid #667085;
  border-bottom: 1.5px solid #667085;
  content: '';
  pointer-events: none;
  transform: translateY(-70%) rotate(45deg);
}

.select-label {
  position: absolute;
  overflow: hidden;
  width: 1px;
  height: 1px;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.language-switcher select {
  min-width: 112px;
  height: 34px;
  padding: 0 30px 0 12px;
  appearance: none;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: transparent;
  color: #344054;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
}

.language-switcher select:focus {
  border-color: #3278fd;
  box-shadow: 0 0 0 3px rgba(50, 120, 253, .14);
}

.player-card {
  padding: 10px;
  border: 1px solid rgba(228, 231, 236, .9);
  border-radius: 24px;
  background: rgba(255, 255, 255, .92);
  box-shadow: 0 22px 60px rgba(16, 24, 40, .12);
}

.player-meta {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 2px 4px 9px;
  color: #98a2b3;
  font-size: 13px;
}

.player-meta span::after {
  content: '·';
  margin-left: 9px;
}

.player-meta strong {
  color: #344054;
  font-weight: 600;
}

.video-frame {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  background: #080d18;
}

.video-frame video {
  display: block;
  width: 100%;
  height: 100%;
  background: #000;
  object-fit: contain;
}

.video-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background:
    radial-gradient(circle at center, rgba(50, 120, 253, .22), transparent 35%),
    linear-gradient(145deg, #111a2d, #080d18);
  color: #fff;
  text-align: center;
}

.video-placeholder::before,
.video-placeholder::after {
  position: absolute;
  width: 260px;
  height: 260px;
  border: 1px solid rgba(255, 255, 255, .06);
  border-radius: 50%;
  content: '';
}

.video-placeholder::after {
  width: 390px;
  height: 390px;
}

.play-mark {
  position: relative;
  z-index: 1;
  display: grid;
  width: 76px;
  height: 76px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, .28);
  border-radius: 50%;
  background: rgba(255, 255, 255, .11);
  box-shadow: 0 16px 32px rgba(0, 0, 0, .3);
  backdrop-filter: blur(8px);
}

.play-mark span {
  width: 0;
  height: 0;
  margin-left: 5px;
  border-top: 11px solid transparent;
  border-bottom: 11px solid transparent;
  border-left: 17px solid #fff;
}

.video-placeholder h2 {
  position: relative;
  z-index: 1;
  margin-top: 22px;
  font-size: 22px;
  font-weight: 700;
}

.video-placeholder p {
  position: relative;
  z-index: 1;
  margin-top: 9px;
  color: #b8c2d6;
  font-size: 14px;
  line-height: 1.7;
  text-align: center;
}

.player-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 4px 1px;
  color: #667085;
  font-size: 13px;
}

.hint-icon {
  display: inline-grid;
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  place-items: center;
  border-radius: 50%;
  background: #eef4ff;
  color: #3278fd;
  font-family: Georgia, serif;
  font-size: 12px;
  font-weight: bold;
}

@media (max-width: 640px) {
  .video-main {
    width: 94%;
    padding: 20px 0;
  }

  .video-hero {
    margin-bottom: 18px;
  }

  .video-hero h1 {
    margin-top: 12px;
    font-size: 30px;
  }

  .video-hero p {
    margin-top: 8px;
    font-size: 15px;
    line-height: 1.65;
  }

  .language-switcher {
    top: 10px;
    right: 0;
  }

  .player-section {
    width: min(94vw, 40vh);
    width: min(94vw, 40dvh);
  }

  .player-card {
    padding: 8px;
    border-radius: 17px;
  }

  .video-frame {
    aspect-ratio: 9 / 16;
    border-radius: 11px;
  }

  .play-mark {
    width: 58px;
    height: 58px;
  }

  .video-placeholder h2 {
    margin-top: 14px;
    font-size: 18px;
  }

  .video-placeholder p {
    margin-top: 5px;
    font-size: 12px;
  }
}

@media (max-height: 650px) and (min-width: 641px) {
  .video-main {
    padding: 12px 0;
  }

  .video-hero {
    margin-bottom: 10px;
  }

  .eyebrow {
    padding: 4px 10px;
    font-size: 11px;
  }

  .video-hero h1 {
    margin-top: 7px;
    font-size: 26px;
  }

  .video-hero p {
    display: none;
  }

  .player-section {
    width: min(880px, 90vw, 90vh);
  }

  .language-switcher {
    top: 7px;
  }

  .language-switcher select {
    height: 30px;
  }

  .player-card {
    padding: 7px;
  }

  .player-meta {
    padding-bottom: 6px;
  }

  .player-hint {
    display: none;
  }
}

@media (max-width: 640px) and (max-height: 720px) {
  .video-main {
    padding: 12px 0;
  }

  .video-hero {
    margin-bottom: 10px;
  }

  .player-section {
    width: min(94vw, 38vh);
    width: min(94vw, 38dvh);
  }

  .eyebrow {
    padding: 4px 10px;
    font-size: 11px;
  }

  .video-hero h1 {
    margin-top: 7px;
    font-size: 25px;
  }

  .video-hero p,
  .player-hint {
    display: none;
  }

  .player-meta {
    padding-bottom: 6px;
  }
}
</style>
